import { useState, useEffect, useRef, useCallback } from "react";
import { MessageSquare, X, Send, Plus, UserPlus, ChevronLeft, Trash2, ImagePlus, Megaphone, CheckCheck } from "lucide-react";
import api, { formatApiError, API, dlToken } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { compressImage } from "@/lib/imageCompress";

export default function ChatDrawer({ user }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [monters, setMonters] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [img, setImg] = useState(null);
  const [sending, setSending] = useState(false);
  const [showBcast, setShowBcast] = useState(false);
  const [bText, setBText] = useState("");
  const [bImg, setBImg] = useState(null);
  const [bSending, setBSending] = useState(false);
  const [rcpt, setRcpt] = useState({});
  const fileRef = useRef(null);
  const bFileRef = useRef(null);
  const endRef = useRef(null);
  const token = dlToken();
  const isBoss = user?.role === "admin" || user?.role === "projektant";
  const fileUrl = (id) => `${API}/files/${id}?auth=${token}`;

  const uploadImage = async (f) => {
    const c = await compressImage(f);
    const fd = new FormData(); fd.append("file", c);
    const { data } = await api.post("/chat/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.file_id;
  };
  const loadReceipts = async (mid) => {
    if (rcpt[mid]) { setRcpt((r) => { const n = { ...r }; delete n[mid]; return n; }); return; }
    try { const { data } = await api.get(`/chat/message-receipts?message_id=${mid}`); setRcpt((r) => ({ ...r, [mid]: data })); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const loadThreads = useCallback(() => api.get("/chat/threads").then((r) => setThreads(r.data || [])).catch(() => {}), []);
  const loadUnread = useCallback(() => api.get("/chat/unread").then((r) => setUnread(r.data.unread || 0)).catch(() => {}), []);
  useEffect(() => { loadUnread(); const id = setInterval(loadUnread, 8000); return () => clearInterval(id); }, [loadUnread]);
  useEffect(() => {
    if (!open) return;
    loadThreads();
    api.get("/users").then((r) => setMonters((r.data || []).filter((u) => u.role === "monter"))).catch(() => {});
  }, [open, loadThreads]);

  const loadMsgs = useCallback((tid) => api.get(`/chat/messages?thread_id=${tid}`).then((r) => setMsgs(r.data || [])).then(() => { loadThreads(); loadUnread(); }).catch(() => {}), [loadThreads, loadUnread]);
  useEffect(() => {
    if (!active) return;
    loadMsgs(active);
    const id = setInterval(() => loadMsgs(active), 4000);
    return () => clearInterval(id);
  }, [active, loadMsgs]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if ((!text.trim() && !img) || !active || sending) return;
    setSending(true);
    try {
      let image_file_id;
      if (img) image_file_id = await uploadImage(img);
      await api.post("/chat/messages", { thread_id: active, text: text.trim(), image_file_id });
      setText(""); setImg(null); loadMsgs(active);
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const sendBroadcast = async () => {
    if ((!bText.trim() && !bImg) || bSending) return;
    setBSending(true);
    try {
      let image_file_id;
      if (bImg) image_file_id = await uploadImage(bImg);
      const { data } = await api.post("/chat/broadcast", { text: bText.trim(), image_file_id });
      toast.success(`${t("Wysłano ogłoszenie do")} ${data.recipients} ${t("monterów")}`);
      setBText(""); setBImg(null); setShowBcast(false); loadThreads();
    }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBSending(false); }
  };
  const startWith = async (mid) => {
    try { const { data } = await api.post("/chat/threads", { monter_id: mid }); setShowNew(false); loadThreads(); setActive(data.id); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addParticipant = async (mid) => {
    try { await api.post(`/chat/threads/${active}/participants`, { monter_id: mid }); toast.success(t("Dodano do rozmowy")); setShowAdd(false); loadThreads(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm(t("Usunąć wiadomość? Kopia pozostaje na serwerze (podgląd w Ustawieniach)."))) return;
    try { await api.delete(`/chat/messages/${id}`); loadMsgs(active); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const at = threads.find((x) => x.id === active);

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-drawer-trigger"
          className={`absolute right-0 z-[745] bg-slate-900/90 backdrop-blur border border-r-0 border-slate-700 rounded-l-lg px-1.5 py-4 hover:bg-slate-800 flex flex-col items-center gap-1 ${unread > 0 ? "text-cyan-300 animate-pulse ring-2 ring-cyan-500" : "text-cyan-400"}`}
          style={{ top: "calc(50% - 74px)" }} title={t("Czat z monterami")}>
          <ChevronLeft size={16} />
          <div className="relative">
            <MessageSquare size={16} />
            {unread > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[9px] leading-none rounded-full px-1 py-0.5" data-testid="chat-unread-badge">{unread}</span>}
          </div>
        </button>
      )}

      {open && (
        <div className="absolute top-0 right-0 bottom-0 w-full sm:w-[380px] z-[770] bg-slate-900 border-l border-slate-700 flex flex-col shadow-2xl" data-testid="chat-drawer">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
            {active && <button onClick={() => setActive(null)} data-testid="chat-back" className="text-slate-400 hover:text-white"><ChevronLeft size={20} /></button>}
            <MessageSquare size={18} className="text-cyan-400" />
            <h3 className="font-heading font-bold text-sm flex-1 truncate">{active ? (at?.name || t("Rozmowa")) : t("Czat z monterami")}</h3>
            {active && at && (
              <button onClick={() => setShowAdd((s) => !s)} data-testid="chat-add-participant-btn" title={t("Dodaj montera (grupa)")} className="text-slate-400 hover:text-cyan-300"><UserPlus size={17} /></button>
            )}
            <button onClick={() => { setOpen(false); setActive(null); }} data-testid="chat-close" className="text-slate-400 hover:text-white"><X size={20} /></button>
          </div>

          {showAdd && active && (
            <div className="p-2 border-b border-slate-800 bg-slate-800/40" data-testid="chat-add-participant-list">
              <div className="text-[11px] text-slate-400 mb-1 px-1">{t("Dołącz montera do wspólnej rozmowy")}</div>
              <div className="max-h-32 overflow-y-auto">
                {monters.filter((m) => !(at?.participant_ids || []).includes(m.id)).map((m) => (
                  <button key={m.id} onClick={() => addParticipant(m.id)} className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-800 rounded" data-testid={`chat-add-${m.id}`}>{m.name}</button>
                ))}
              </div>
            </div>
          )}

          {!active ? (
            <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
              <button onClick={() => setShowNew((s) => !s)} data-testid="chat-new-btn"
                className="w-full flex items-center gap-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg px-3 py-2 text-sm mb-1"><Plus size={16} /> {t("Nowa rozmowa z monterem")}</button>
              {isBoss && (
                <button onClick={() => setShowBcast((s) => !s)} data-testid="chat-broadcast-btn"
                  className="w-full flex items-center gap-2 bg-amber-600 hover:bg-amber-500 rounded-lg px-3 py-2 text-sm mb-1"><Megaphone size={16} /> {t("Ogłoszenie do wszystkich monterów")}</button>
              )}
              {isBoss && showBcast && (
                <div className="bg-slate-800 border border-amber-700/50 rounded-lg p-2 mb-2 space-y-2" data-testid="chat-broadcast-panel">
                  <div className="text-[11px] text-amber-300 px-0.5">{t("Wiadomość trafi do wszystkich monterów naraz (jednokierunkowo).")}</div>
                  <textarea value={bText} onChange={(e) => setBText(e.target.value)} rows={2} placeholder={t("Treść ogłoszenia…")}
                    data-testid="chat-broadcast-input" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-amber-500 resize-none" />
                  {bImg && <div className="text-[11px] text-slate-400 flex items-center gap-2">📷 {bImg.name} <button onClick={() => setBImg(null)} className="text-red-400">✕</button></div>}
                  <input ref={bFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setBImg(e.target.files?.[0] || null)} data-testid="chat-broadcast-file" />
                  <div className="flex gap-2">
                    <button onClick={() => bFileRef.current?.click()} className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600" title={t("Dodaj zdjęcie")}><ImagePlus size={16} /></button>
                    <button onClick={sendBroadcast} disabled={bSending} data-testid="chat-broadcast-send"
                      className="flex-1 flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 rounded-lg px-3 py-2 text-sm font-medium"><Megaphone size={15} /> {bSending ? t("Wysyłanie…") : t("Wyślij ogłoszenie")}</button>
                  </div>
                </div>
              )}
              {showNew && (
                <div className="bg-slate-800 border border-slate-700 rounded-lg p-1 mb-2" data-testid="chat-new-list">
                  {monters.length === 0 && <div className="text-[11px] text-slate-500 p-2">{t("Brak monterów w systemie.")}</div>}
                  {monters.map((m) => (
                    <button key={m.id} onClick={() => startWith(m.id)} data-testid={`chat-start-${m.id}`} className="w-full text-left px-2 py-1.5 text-sm hover:bg-slate-700 rounded">{m.name}</button>
                  ))}
                </div>
              )}
              {threads.length === 0 && <div className="text-slate-500 text-sm text-center py-10">{t("Brak rozmów.")}</div>}
              {threads.map((th) => (
                <button key={th.id} onClick={() => setActive(th.id)} data-testid={`chat-thread-${th.id}`}
                  className="w-full text-left bg-slate-800/60 border border-slate-700 rounded-lg p-2.5 flex items-center gap-2 hover:border-cyan-600">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate flex items-center gap-1.5">{th.is_group && <span className="text-[9px] bg-slate-700 rounded px-1">GRUPA</span>}{th.name}</div>
                    <div className="text-xs text-slate-400 truncate">{th.last_message || "—"}</div>
                  </div>
                  {th.unread > 0 && <span className="bg-red-500 text-white text-[10px] rounded-full px-1.5 py-0.5">{th.unread}</span>}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="chat-messages">
                {msgs.map((m) => (
                  <div key={m.id} className={`group relative max-w-[82%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user.id ? "ml-auto bg-cyan-600 text-white" : "bg-slate-800 border border-slate-700 text-slate-100"}`}>
                    {m.sender_id !== user.id && <div className="text-[10px] text-cyan-300 mb-0.5">{m.sender_name}</div>}
                    {m.deleted ? <div className="italic opacity-60">{t("Wiadomość usunięta")}</div>
                      : <>
                          {m.image_file_id && <a href={fileUrl(m.image_file_id)} target="_blank" rel="noreferrer" data-testid={`chat-img-${m.id}`}><img src={fileUrl(m.image_file_id)} alt="" className="rounded-lg max-w-full mb-1 max-h-60 object-cover" /></a>}
                          {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                        </>}
                    <div className="text-[9px] opacity-60 mt-0.5 text-right">{(m.ts || "").slice(11, 16)}</div>
                    {at?.kind === "broadcast" && m.sender_id === user.id && !m.deleted && (
                      <div className="mt-1">
                        <button onClick={() => loadReceipts(m.id)} data-testid={`chat-receipts-${m.id}`}
                          className="text-[10px] opacity-80 hover:opacity-100 underline flex items-center gap-1"><CheckCheck size={11} /> {rcpt[m.id] ? `${t("odczytało")} ${rcpt[m.id].read.length}/${rcpt[m.id].total}` : t("odczyty")}</button>
                        {rcpt[m.id] && (
                          <div className="mt-1 text-[10px] bg-black/25 rounded p-1.5 space-y-0.5" data-testid={`chat-receipts-panel-${m.id}`}>
                            {rcpt[m.id].read.length > 0 && <div className="text-green-200">✓ {rcpt[m.id].read.map((x) => x.name).join(", ")}</div>}
                            {rcpt[m.id].unread.length > 0 && <div className="opacity-70">◻ {rcpt[m.id].unread.map((x) => x.name).join(", ")}</div>}
                            {rcpt[m.id].total === 0 && <div className="opacity-70">{t("Brak monterów")}</div>}
                          </div>
                        )}
                      </div>
                    )}
                    {!m.deleted && (
                      <button onClick={() => del(m.id)} data-testid={`chat-del-${m.id}`} title={t("Usuń wiadomość")}
                        className="absolute -left-7 top-1 text-slate-500 hover:text-red-400 opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>
                    )}
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <div className="p-2 border-t border-slate-700" data-testid="chat-composer">
                {at?.kind === "broadcast" && at?.name === "📢 Ogłoszenia biura" && !isBoss ? (
                  <div className="text-center text-xs text-slate-500 py-2">{t("Kanał ogłoszeń — tylko do odczytu")}</div>
                ) : (
                  <>
                    {img && <div className="text-[11px] text-slate-400 flex items-center gap-2 mb-1.5 px-1">📷 {img.name} <button onClick={() => setImg(null)} data-testid="chat-img-clear" className="text-red-400">✕</button></div>}
                    <div className="flex gap-2">
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => setImg(e.target.files?.[0] || null)} data-testid="chat-file-input" />
                      <button onClick={() => fileRef.current?.click()} data-testid="chat-attach" className="p-2.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-300" title={t("Dodaj zdjęcie")}><ImagePlus size={17} /></button>
                      <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
                        placeholder={t("Napisz wiadomość…")} data-testid="chat-input" className="flex-1 bg-slate-800 border border-slate-700 rounded-full px-4 py-2 text-sm outline-none focus:ring-1 focus:ring-cyan-500" />
                      <button onClick={send} disabled={sending} data-testid="chat-send" className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-full p-2.5"><Send size={17} /></button>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
